/* Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { NvHigListMetadata } from '../../models/hig-list-metadata.types';
import {
    NvDialogButton,
    NvDialogCheckbox,
    NvDialogImage,
    NvDialogLink,
    NvHigIconTextDescription,
    NvHigRadioButtonOptions,
    NvHigText,
    NvHigTranslateValues,
    RestoreFocusOptions,
    NvHigCellComparison,
    NvHigTableColumns
} from '../../models/hig-modal-shared.types';
import { ColorThemes, NvHigListItemData, NvHigListInsertData } from '../../models/hig-list-item.types';
import { NvHigBottomSheetComponent } from '../nv-hig-bottom-sheet/nv-hig-bottom-sheet.component';
import { NvHigDialogComponent } from '../nv-hig-dialog/nv-hig-dialog.component';
import { NvHigThreeQuarterSheetComponent } from '../nv-hig-three-quarter-sheet/nv-hig-three-quarter-sheet.component';

// Common used config for MatDialogConfig and MatBottomSheetConfig
class NvHigCommonConfig<D = any> {
    closeOnNavigation?: boolean;
    panelClass?: string | string[];
    hasBackdrop?: boolean;
    backdropClass?: string;
    disableClose?: boolean;
    maxWidth?: string;
    autoFocus?: boolean;
    ariaLabel?: string;
    data?: D | null;
}

export class NvGenericDialogData {
    icon?: string;
    headerText?: NvHigText;
    /** Semantic heading level for headerText. When set, renders headerText with role="heading". @default undefined (no heading role) */
    headerHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
    bodyText?: NvHigText;
    /** Optional SVG icon rendered inline ahead of bodyText (e.g. 'warning' for destructive confirmations) */
    bodyIcon?: string;
    /** Colour theme applied to bodyIcon. Inherits the body text colour when unset. */
    bodyIconColorTheme?: ColorThemes;
    /** Body text rendered above the QR code/content section (for layouts like resync prompt) */
    topBodyText?: NvHigText;
    footerText?: NvHigText;
    errorCodeText?: NvHigText;
    errorSubtext?: NvHigText;
    link?: NvDialogLink;
    image?: NvDialogImage;
    primaryButton?: NvDialogButton;
    secondaryButton?: NvDialogButton;
    checkbox?: NvDialogCheckbox;
    translateValues?: NvHigTranslateValues = {};
    isTransparent?: boolean;
    radioButtonOptions?: NvHigRadioButtonOptions;
    checkboxState?: boolean;
    radioOptionSelected?: string;
    showSpinner?: boolean;
    autoFocusOnPrimaryButton?: boolean;
    /**
     * Specifies which button type should receive auto-focus when the dialog opens.
     * Takes precedence over `autoFocusOnPrimaryButton` when set.
     *
     * Resolution hierarchy (when not explicitly set):
     *   1. radioButtonOptions  → first radio button
     *   2. primaryButton       → primary action button
     *   3. augmentedButtons    → first augmented button
     *   4. 'none'              → no auto-focus
     *
     * Set to 'none' to disable auto-focus entirely.
     */
    autoFocusTarget?: 'primary' | 'radio' | 'augmented' | 'none';
    dataCells?: NvHigCellComparison[];
    dataTableColumns?: NvHigTableColumns[];
    metadata?: NvHigListMetadata;
    fullscreenPanelForSmallScreen?: boolean;
    /**
     * By default, HIG modals can be closed by Back buttons (Keyboard Escape, Gamepad Button B, TV remote back... etc).
     * Set this flag to true to disable the back-to-close behavior.
     */
    disableBackToClose?: boolean;
    /** Consume every Back key without closing the modal. Requires back-to-close handling to remain enabled. */
    consumeBackWithoutClosing?: boolean;
    threeQuarterSheetData?: NvHigThreeQuarterSheetData;
    descriptionList?: NvHigIconTextDescription[];
    /**
     * This is a temporary WAR added to handle the focus issue on dialog over the dialog scenario.
     * UX is working on the change to revamp NT Stage so once this pattern is removed, the change can be reverted.
     * Also, the issue is no repro with Spatial Navigation enabled, so we can retire this change once Spatial Nav
     * is enabled on all platforms.
     */
    trapFocus?: boolean;
    spinnerClass?: string;
    removeButtonTextOnClick?: boolean;
    /** When true, shows spinner in the button area (right side) instead of content area */
    showSpinnerInButtonArea?: boolean;
    /** List insert data for inline alerts/banners (uses nv-hig-list-insert component) */
    listInsertData?: NvHigListInsertData;
    /** Structured account card data for nv-hig-list-item rendering (used by ownership flow on TV) */
    higCardData?: NvHigListItemData[];
}

export enum PreferredDialogType {
    ThreeQuarterSheet = 'ThreeQuarterSheet',
    BottomSheet = 'BottomSheet'
}

export interface OpenGenericDialogOptions extends RestoreFocusOptions {
    preferredDialogType?: PreferredDialogType;
}

export type NvGenericDialogConfig = NvHigCommonConfig<NvGenericDialogData>;
export type NvHigDialogRef = MatDialogRef<NvHigDialogComponent>;
export type NvHigBottomSheetRef = MatBottomSheetRef<NvHigBottomSheetComponent>;
export type NvHigThreeQuarterSheetRef = MatDialogRef<NvHigThreeQuarterSheetComponent>;

export interface AlertTimeoutIntervals {
    /** Ratio (0-1) for when halfway warning should fire. Default: 0.5 (50% elapsed) */
    halfwayWarningRatio?: number;
    /** Milliseconds remaining when last countdown warning should fire. Default: 60000 (1 minute) */
    lastCountdownRemainingMs?: number;
}

export interface TimeoutNotificationConfig {
    /** Total timeout duration in milliseconds before dialog auto-closes */
    totalDuration: number;
    /** Translation key for warning snackbar messages (used for initial, halfway, and last countdown warnings) */
    warningMessageKey: string;
    /** Translation key for timeout expired snackbar message */
    expiredMessageKey: string;
    /** Optional configuration for customizing alert timeout intervals */
    alertTimeoutIntervals?: AlertTimeoutIntervals;
    /** Delay in milliseconds before closing dialog after timeout expires. Default: 2000ms. Set to 0 to close immediately. */
    dialogCloseDelayMs?: number;
}

export interface NvHigSubtitleTextPart {
    type: 'text';
    text: string;
    /** When true, text is already translated and should not go through the translate pipe */
    translated?: boolean;
    /** When true, hides this part from screen readers (e.g. decorative separators like ' | ') */
    ariaHidden?: boolean;
}

export interface NvHigSubtitleIconPart {
    type: 'icon';
    imageUrl: string;
}

export type NvHigSubtitlePart = NvHigSubtitleTextPart | NvHigSubtitleIconPart;

export interface NvHigSubtitle {
    parts: NvHigSubtitlePart[];
    status?: string;
    /** Whether to show a check icon after the status tag */
    showCheckIcon?: boolean;
}

export interface NvHigThreeQuarterSheetData {
    /**
     * augmentedButtons to be used when there are more than two buttons in the 3/4 sheet,
     * and they will be placed below the secondary button.
     */
    augmentedButtons?: NvDialogButton[];
    backgroundImageUrl?: string;
    title?: NvHigText;
    /** Semantic heading level for the title. @default 2 */
    titleHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
    /** Subtitle below the title: ordered list of text/icon parts */
    subtitle?: NvHigSubtitle;
    /** When true, disables the translucent black overlay on content and button containers */
    disableTranslucent?: boolean;
    /** Configuration for showing timeout warning snackbars and auto-closing dialog */
    timeoutNotificationConfig?: TimeoutNotificationConfig;
}

const ICON_WIDTH = 32;
const LARGE_ICON_WIDTH = 112;

export function getIconWidth(largeIcon = false) {
    return largeIcon ? LARGE_ICON_WIDTH : ICON_WIDTH;
}

export function addPanelClass(
    config: NvGenericDialogConfig,
    params: { addOverlayPanelCls?: boolean; bottomSheet?: boolean; threeQuarterSheet?: boolean; tvMode?: boolean }
) {
    const classNames = [];
    if (params.addOverlayPanelCls) {
        classNames.push('nv-overlay-panel');
    }
    if (params.bottomSheet || params.threeQuarterSheet) {
        classNames.push('nv-hig');
    }
    if (params.threeQuarterSheet) {
        classNames.push('nv-three-quarter-sheet');
    }
    if (params.tvMode) {
        classNames.push('is-tv');
    }
    if (classNames.length > 0) {
        const panelClass = config.panelClass
            ? config.panelClass instanceof Array
                ? config.panelClass
                : new Array(config.panelClass)
            : [];
        classNames.forEach(name => {
            if (!panelClass.includes(name)) {
                panelClass.push(name);
            }
        });
        config.panelClass = panelClass;
    }
}
